import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell,
  TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Chart as ChartJS, BarController, LineController } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { SectionCard } from './SectionCard';
import { formatMoney } from '../utils/money';

/** Fractions of a cent matter here: a stats check is half a cent */
const money4 = (n: number | null | undefined) =>
  n == null ? '—' : n < 0.1 && n > 0 ? `$${n.toFixed(3)}` : formatMoney(n);
import { useAuth } from '../contexts/AuthContext';
import { FullTextTooltip } from './FullTextTooltip';

interface Purchase { id: string; purchased_on: string; amount: number; note: string | null; transaction_ref: string | null }

interface RequestDay {
  day: string;
  source: 'app' | 'console';
  read: number;
  post_create: number;
  content_manage: number;
  media: number;
  other: number;
  total: number;
}
type RequestTotals = Omit<RequestDay, 'day' | 'source'>;

/** The console's request kinds, in its order, with the colours used for them here. */
const KINDS: { key: keyof RequestTotals; label: string; color: string }[] = [
  { key: 'read', label: 'Read', color: 'rgb(29, 155, 240)' },
  { key: 'post_create', label: 'Post create', color: 'rgb(16, 185, 129)' },
  { key: 'content_manage', label: 'Content manage', color: 'rgb(245, 158, 11)' },
  { key: 'media', label: 'Media upload', color: 'rgb(139, 92, 246)' },
  { key: 'other', label: 'Other', color: 'rgb(148, 163, 184)' },
];

interface XCreditsData {
  configured: boolean;
  last_checked_at: string | null;
  last_error: string | null;
  days: number;
  balance: { total: number; prepaid: number | null; free: number | null; recorded_at: string } | null;
  low: boolean;
  low_threshold: number;
  paid_total: number;
  used_total: number | null;
  window: { spent: number | null; purchased: number; x_posts: number; per_post: number | null; posts_read: number | null };
  history: { recorded_at: string; total_balance: number }[];
  purchases: Purchase[];
  requests: { totals: RequestTotals; by_day: RequestDay[] };
  daily_cost: { day: string; spent: number }[];
  cost_model: CostModel;
  console_report: {
    captured_at: string; period_from: string | null; period_to: string | null;
    billable_events: number | null; total_cost: number | null; requests: number | null;
  } | null;
}

interface PostCost {
  post_id: string; tweet_id: string; permalink: string; published_at: string; excerpt: string; text: string;
  has_link: boolean; create_cost: number; read_days: number; read_days_estimated: number;
  read_cost: number; total_cost: number; likes: number; comments: number; shares: number;
  views: number | null; engagements: number; cost_per_engagement: number | null;
}
interface CostModel {
  pricing: { post_read: number; user_read: number; post_create: number; post_create_url: number; source: string; as_of: string };
  tracking_since: string;
  estimate: {
    posts_created: number; posts_created_with_link: number; create_cost: number; post_read_days: number;
    read_cost: number; account_read_days: number; account_read_cost: number; total: number;
  };
  posts: PostCost[];
}

interface ImportResult { days: number; payments_added: number; payments_known: number; balance: boolean; report: boolean }

// The requests chart mixes bars and a cost line; both controllers must be registered whichever
// chart on the page happens to load first.
ChartJS.register(BarController, LineController);

const shortDay = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const today = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

/**
 * What posting to X through its API costs (2026-10-18). Admins only.
 *
 * X sells API access as prepaid credit and its API reports only the balance left, so "paid" is
 * the purchases entered here and "used" is paid less the purchased balance left. The spend over
 * the period comes from the balance samples the server takes every few hours.
 */
export const XCredits: React.FC<{ days: number }> = ({ days }) => {
  const { token } = useAuth();
  const [data, setData] = useState<XCreditsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [note, setNote] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [imported, setImported] = useState<ImportResult | null>(null);
  // The per-post cost table pages: a year of X posts is hundreds of rows
  const [costPage, setCostPage] = useState(0);
  const [costRows, setCostRows] = useState(10);
  useEffect(() => { setCostPage(0); }, [days]);

  const call = useCallback(async (path: string, init?: RequestInit) => {
    const res = await fetch(`${path}${path.includes('?') ? '&' : '?'}days=${days}`, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new Error(body?.message || body?.error || `Request failed (${res.status})`);
    setData(body);
    setError(null);
    return body;
  }, [days, token]);

  useEffect(() => { call('/api/x-credits').catch(e => setError(e.message)); }, [call]);

  const run = (path: string, init?: RequestInit) => {
    setBusy(true);
    return call(path, init).catch(e => { setError(e.message); throw e; }).finally(() => setBusy(false));
  };

  const addPurchase = () => run('/api/x-credits/purchases', {
    method: 'POST',
    body: JSON.stringify({ amount: Number(amount), purchased_on: date, note }),
  }).then(() => { setAmount(''); setNote(''); }).catch(() => {});

  const importConsole = () => {
    setBusy(true);
    call('/api/x-credits/import', { method: 'POST', body: JSON.stringify({ text: pasteText }) })
      .then(body => { setImported(body.imported); setPasteOpen(false); setPasteText(''); })
      .catch(e => setError(e.message))
      .finally(() => setBusy(false));
  };

  const b = data?.balance;
  const figures: [string, string][] = data ? [
    [b ? formatMoney(b.total) : '—', 'left'],
    [formatMoney(data.paid_total), 'paid'],
    [data.used_total != null ? formatMoney(data.used_total) : '—', 'used'],
    [data.window.spent != null ? formatMoney(data.window.spent) : '—', `spent in ${data.days} days`],
    [String(data.window.x_posts), `X post${data.window.x_posts === 1 ? '' : 's'} in ${data.days} days`],
    [data.window.per_post != null ? formatMoney(data.window.per_post) : '—', 'per X post'],
  ] : [];

  const reqDays = data?.requests.by_day || [];
  const costByDay = new Map((data?.daily_cost || []).map(c => [c.day, c.spent]));
  const usedKinds = KINDS.filter(k => (data?.requests.totals[k.key] || 0) > 0);
  const requestChart = reqDays.length ? {
    labels: reqDays.map(d => shortDay(d.day)),
    datasets: [
      ...usedKinds.map(k => ({
        type: 'bar' as const,
        label: k.label,
        data: reqDays.map(d => d[k.key]),
        backgroundColor: k.color,
        stack: 'requests',
        yAxisID: 'y',
      })),
      ...(costByDay.size ? [{
        type: 'line' as const,
        label: 'Cost',
        data: reqDays.map(d => costByDay.get(d.day) ?? null),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgb(239, 68, 68)',
        borderWidth: 2,
        pointRadius: 2,
        spanGaps: true,
        yAxisID: 'cost',
      }] : []),
    ],
  } : null;
  const fromConsole = reqDays.filter(d => d.source === 'console').length;
  const report = data?.console_report;

  const chart = data && data.history.length > 1 ? {
    labels: data.history.map(h => new Date(h.recorded_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })),
    datasets: [{
      label: 'Credit left',
      data: data.history.map(h => h.total_balance),
      borderColor: 'rgb(29, 155, 240)',
      backgroundColor: 'rgba(29, 155, 240, 0.1)',
      borderWidth: 2,
      stepped: true,
      fill: true,
      pointRadius: 0,
      pointHoverRadius: 4,
    }],
  } : null;

  return (
    <SectionCard
      id="x-credits"
      title="X API Credit"
      description="X charges ShoutVector for every post it creates and every day it checks a post's stats, from prepaid credit. Only admins see this tab."
      actions={data && (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Paste figures copied from the X Developer Console">
            <Button size="small" startIcon={<ContentPasteIcon fontSize="small" />} onClick={() => setPasteOpen(true)}>
              Paste from X console
            </Button>
          </Tooltip>
          {data.configured && (
            <Tooltip title="Check the balance with X now">
              <span>
                <IconButton size="small" disabled={busy} onClick={() => run('/api/x-credits/refresh', { method: 'POST' }).catch(() => {})}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      )}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {imported && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setImported(null)}>
          Imported from the X console:{' '}
          {[
            imported.days ? `${imported.days} day${imported.days === 1 ? '' : 's'} of requests` : null,
            imported.payments_added ? `${imported.payments_added} payment${imported.payments_added === 1 ? '' : 's'}` : null,
            imported.payments_known ? `${imported.payments_known} payment${imported.payments_known === 1 ? '' : 's'} already recorded` : null,
            imported.balance ? 'the balance' : null,
            imported.report ? 'the period totals' : null,
          ].filter(Boolean).join(', ')}.
        </Alert>
      )}
      {!data ? (
        !error && <Typography variant="body2" color="text.secondary">Loading…</Typography>
      ) : (
        <>
          {!data.configured && (
            <Alert severity="info" sx={{ mb: 2 }}>
              The balance isn't being read from X automatically yet: the server needs X's app-only Bearer Token
              (<code>X_BEARER_TOKEN</code>), from the X Developer Console. Meanwhile, paste the console's pages here
              with <strong>Paste from X console</strong>, or enter purchases below.
            </Alert>
          )}
          {data.configured && data.last_error && (
            <Alert severity="warning" sx={{ mb: 2 }}>Couldn't read the balance from X: {data.last_error}</Alert>
          )}
          {data.low && b && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Credit is down to {formatMoney(b.total)}. Posts to X fail when it runs out.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: { xs: 2.5, sm: 4 }, flexWrap: 'wrap', mb: 2 }}>
            {figures.map(([value, label]) => (
              <Box key={label}>
                <Typography variant="h6">{value}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Box>
            ))}
          </Box>

          {report && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>X's own figures</strong>
              {report.period_from && report.period_to ? ` for ${shortDay(report.period_from)} – ${shortDay(report.period_to)}` : ''}:{' '}
              {[
                report.total_cost != null ? `${formatMoney(report.total_cost)} cost` : null,
                report.billable_events != null ? `${report.billable_events.toLocaleString()} billable events` : null,
                report.requests != null ? `${report.requests.toLocaleString()} API requests` : null,
              ].filter(Boolean).join(' · ')}
              <Typography component="span" variant="caption" color="text.secondary">
                {' '}(pasted {new Date(report.captured_at.replace(' ', 'T') + (report.captured_at.includes('Z') ? '' : 'Z')).toLocaleDateString()})
              </Typography>
            </Typography>
          )}

          {requestChart && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Requests by kind</Typography>
              <Box sx={{ display: 'flex', gap: { xs: 2.5, sm: 4 }, flexWrap: 'wrap', mb: 1.5 }}>
                {[...usedKinds, { key: 'total' as const, label: 'Total requests', color: '' }].map(k => (
                  <Box key={k.key}>
                    <Typography variant="h6">{(data.requests.totals[k.key] || 0).toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                  </Box>
                ))}
                {data.window.posts_read != null && (
                  <Box>
                    <Typography variant="h6">{data.window.posts_read.toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary">billed reads, X's count</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ height: { xs: 200, sm: 240 }, mb: 2 }}>
                <Bar
                  data={requestChart as never}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: { legend: { position: 'bottom', labels: { usePointStyle: true } } },
                    scales: {
                      x: { stacked: true, ticks: { maxRotation: 0, autoSkipPadding: 20 } },
                      y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
                      ...(costByDay.size ? {
                        cost: {
                          position: 'right' as const, beginAtZero: true, grid: { display: false },
                          ticks: { callback: (v: string | number) => formatMoney(Number(v)) },
                        },
                      } : {}),
                    },
                  }}
                />
              </Box>
            </>
          )}

          {chart && (
            <Box sx={{ height: { xs: 180, sm: 220 }, mb: 2 }}>
              <Line
                data={chart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true, ticks: { callback: v => formatMoney(Number(v)) } } },
                }}
              />
            </Box>
          )}

          {data.cost_model && (() => {
            const cm = data.cost_model;
            const e = cm.estimate;
            return (
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>What each X post cost</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  X charges {money4(cm.pricing.post_create)} to create a post ({money4(cm.pricing.post_create_url)} if it
                  contains a link) and {money4(cm.pricing.post_read)} for each day ShoutVector checks its stats, for
                  30 days. For these {data.days} days that comes to <strong>{money4(e.total)}</strong>:{' '}
                  {e.posts_created} post{e.posts_created === 1 ? '' : 's'} created ({money4(e.create_cost)}
                  {e.posts_created_with_link ? `, ${e.posts_created_with_link} with a link` : ''}), {e.post_read_days} days
                  of stats checks ({money4(e.read_cost)}){e.account_read_days ? `, and ${e.account_read_days} days of account checks (${money4(e.account_read_cost)})` : ''}.
                  {report?.total_cost != null && ` X's own figure for ${report.period_from && report.period_to ? `${shortDay(report.period_from)} – ${shortDay(report.period_to)}` : 'its period'} was ${formatMoney(report.total_cost)}.`}
                </Typography>
                {cm.posts.length > 0 ? (
                  <Box sx={{ overflowX: 'auto', mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Published</TableCell>
                          <TableCell>Post</TableCell>
                          <TableCell align="right">Create</TableCell>
                          <TableCell align="right">Stats checks</TableCell>
                          <TableCell align="right">Cost</TableCell>
                          <TableCell align="right">Likes</TableCell>
                          <TableCell align="right">Comments</TableCell>
                          <TableCell align="right">Shares</TableCell>
                          <TableCell align="right">Views</TableCell>
                          <TableCell align="right">Per engagement</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cm.posts.slice(costPage * costRows, costPage * costRows + costRows).map(p => (
                          <TableRow key={p.tweet_id}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{shortDay(String(p.published_at).slice(0, 10))}</TableCell>
                            <TableCell sx={{ maxWidth: 260 }}>
                              <FullTextTooltip text={p.text}>
                                <Typography variant="body2" noWrap>
                                  <a href={p.permalink} target="_blank" rel="noopener noreferrer">{p.text || p.tweet_id}</a>
                                </Typography>
                              </FullTextTooltip>
                            </TableCell>
                            <TableCell align="right">
                              {money4(p.create_cost)}
                              {p.has_link && <Typography component="span" variant="caption" color="warning.main"> link</Typography>}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={p.read_days_estimated ? `${p.read_days_estimated} of these days are estimated — reads are recorded from ${shortDay(cm.tracking_since)}` : 'Recorded'}>
                                <span>{p.read_days} day{p.read_days === 1 ? '' : 's'} · {money4(p.read_cost)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right"><strong>{money4(p.total_cost)}</strong></TableCell>
                            <TableCell align="right">{p.likes.toLocaleString()}</TableCell>
                            <TableCell align="right">{p.comments.toLocaleString()}</TableCell>
                            <TableCell align="right">{p.shares.toLocaleString()}</TableCell>
                            <TableCell align="right">{p.views != null ? p.views.toLocaleString() : '—'}</TableCell>
                            <TableCell align="right">{money4(p.cost_per_engagement)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {cm.posts.length > 10 && (
                      <TablePagination
                        component="div"
                        count={cm.posts.length}
                        page={Math.min(costPage, Math.max(0, Math.ceil(cm.posts.length / costRows) - 1))}
                        onPageChange={(_, p) => setCostPage(p)}
                        rowsPerPage={costRows}
                        onRowsPerPageChange={e => { setCostRows(parseInt(e.target.value, 10)); setCostPage(0); }}
                        rowsPerPageOptions={[10, 25, 50]}
                        labelRowsPerPage="Posts per page"
                      />
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No X posts were published in this period.</Typography>
                )}
              </>
            );
          })()}

          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Purchases</Typography>
          {data.purchases.length === 0 && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              No purchases recorded yet. X's API doesn't report payments, so click <strong>Paste from X console</strong> above
              and paste the console's <strong>Billing → Payments</strong> page, or enter one below.
            </Alert>
          )}
          {data.purchases.length > 0 && (
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.purchases.map(p => (
                  <TableRow key={p.id}>
                    <TableCell>{new Date(`${p.purchased_on}T12:00:00Z`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</TableCell>
                    <TableCell align="right">{formatMoney(p.amount)}</TableCell>
                    <TableCell>{p.note || ''}</TableCell>
                    <TableCell align="right" padding="checkbox">
                      <IconButton size="small" aria-label="Remove purchase" disabled={busy}
                        onClick={() => run(`/api/x-credits/purchases/${p.id}`, { method: 'DELETE' }).catch(() => {})}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" label="Amount ($)" type="number" value={amount}
              onChange={e => setAmount(e.target.value)} sx={{ width: 120 }} slotProps={{ htmlInput: { min: 0, step: '0.01' } }} />
            <TextField size="small" label="Date" type="date" value={date}
              onChange={e => setDate(e.target.value)} sx={{ width: 160 }} slotProps={{ inputLabel: { shrink: true } }} />
            <TextField size="small" label="Note" value={note} onChange={e => setNote(e.target.value)} sx={{ flex: '1 1 160px' }} />
            <Button variant="outlined" disabled={busy || !(Number(amount) > 0) || !date} onClick={addPurchase}>Add purchase</Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            X's API reports only the balance left, so purchases are entered here or pasted from the console's Payments
            page, and "used" is paid less the purchased credit left. Spend and cost per day come from balance checks
            every six hours; per post divides spend by every X post from all workspaces. Requests are ShoutVector's own
            count of its X calls{fromConsole ? `, except ${fromConsole} day${fromConsole === 1 ? '' : 's'} pasted from the X console, which use X's count` : ''}.
            Nearly all are reads checking post stats; X bills a repeated read of the same post far less often than it is made.
            {(data.last_checked_at || b) && ` Balance last read ${new Date(data.last_checked_at || b!.recorded_at).toLocaleString()}.`}
          </Typography>
        </>
      )}

      <Dialog open={pasteOpen} onClose={() => setPasteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Paste from the X console</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            X's API does not report cost, requests by kind or payments, so copy them from the X Developer Console:
            select all on the <strong>Usage</strong>, <strong>Credits</strong> or <strong>Payments</strong> page, copy,
            and paste here — one page or several at once. Pasting the same page again updates it rather than doubling it.
          </Typography>
          <TextField
            multiline minRows={8} maxRows={16} fullWidth autoFocus value={pasteText}
            onChange={e => setPasteText(e.target.value)} placeholder="Usage analytics&#10;Showing data from …"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasteOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !pasteText.trim()} onClick={importConsole}>Import</Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};
